import { classNames } from "@/utils/classNames";
import { ChangeEvent, ChangeEventHandler, FocusEvent, ReactElement } from "react";
import { Box } from "./Box";
import { observer } from "mobx-react";
import { Alert } from "./Alert";
import { isTMessage, TMessage, TMessageParam } from "@/i18n/tt";

import { useTranslation } from "@/stores/RootStoreContext";
import { RadioFieldModel } from "@/common/commonForms";
import { CommonProps } from "./boxHelpers";
import { isReactElement } from "@/utils/reactHelpers";

export type RadioButtonProps = {
  name?: string;
  label?: ReactElement | string | number | TMessage | TMessageParam;
  description?: ReactElement | string | number | TMessage;
  checked?: boolean;
  disabled?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
};

export function RadioButton({
  name,
  label,
  description,
  checked,
  disabled,
  onChange,
}: RadioButtonProps) {
  const { t, tf } = useTranslation();
  const radioButton = (
    <div className={"relative w-5 h-5"}>
      <input
        type="checkbox"
        className={classNames(
          "absolute",
          "appearance-none",
          "w-5",
          "h-5",
          "border-2",
          "rounded-full",
          "bg-default",
          disabled && ["cursor-default", "border-gray"],
          !disabled &&
            checked && [
              "cursor-pointer",
              "border-primary",
              "hover:border-primary-dark",
              "cursor-pointer",
            ],
          !disabled &&
            !checked && ["border-gray-hard", "hover:border-primary", "hover:bg-primary-light"],
        )}
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <div
        className={classNames(
          "absolute",
          "w-3",
          "h-3",
          "inset-1",
          "rounded-full",
          "border",
          "border-primary-contrast",
          "pointer-events-none",
          checked ? "block" : "hidden",
          disabled ? ["bg-gray"] : ["bg-primary", "hover:bg-primary-dark"],
        )}
      ></div>
    </div>
  );

  return label ? (
    <label
      className={classNames(
        "flex",
        "gap-2",
        "items-start",
        disabled ? ["cursor-default", "subtle"] : ["cursor-pointer", "color-default"],
      )}
    >
      <div
        style={{
          paddingTop: 2 /* yuck! need to figure out a better solution to this (this happens when the label wraps) */,
        }}
      >
        {radioButton}
      </div>
      {description ? (
        <div>
          <div>{isReactElement(label) ? label : tf(label)}</div>
          <div className={"text-sm subtle"}>
            {isTMessage(description) ? t(description) : description}
          </div>
        </div>
      ) : (
        <div>{isReactElement(label) ? label : tf(label)}</div>
      )}
    </label>
  ) : (
    radioButton
  );
}

export type RadioButtonGroupProps<T> = {
  disabled?: boolean;
  horizontal?: boolean;
  idFn: (item: T) => string;
  labelFn: (item: T) => string | ReactElement | number | TMessage;
  descriptionFn?: (item: T) => (ReactElement | string | number) | undefined | TMessage;
  disabledFn?: (item: T) => boolean;
  name?: string;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>, selected: T) => void;
  options: T[];
  selected?: T;
  error?: TMessage;
  gap?: CommonProps["gap"];
};

export const RadioButtonGroup = observer(function RadioButtonGroup<T>({
  name,
  horizontal,
  options,
  selected,
  idFn,
  labelFn,
  descriptionFn,
  disabledFn,
  disabled,
  onChange,
  error,
}: RadioButtonGroupProps<T>) {
  const { t } = useTranslation();
  return (
    <>
      <Box vertical={!horizontal} gap={horizontal ? "md" : "sm"}>
        {options.map((option) => (
          <RadioButton
            key={idFn(option)}
            name={name}
            disabled={disabled || disabledFn?.(option)}
            label={labelFn(option)}
            description={descriptionFn?.(option)}
            onChange={(event) => {
              if (event.target.checked) {
                onChange?.(event, option);
              }
            }}
            checked={selected !== undefined && idFn(selected) === idFn(option)}
          />
        ))}
      </Box>
      {error !== undefined && <Alert type={"error"} message={<>{t(error)}</>} />}
    </>
  );
});

export const MRadioButtonGroup = observer(function MRadioButtonGroup<T>({
  model,
  name,
  options,
  idFn,
  labelFn,
  descriptionFn,
  disabledFn,
  disabled,
  horizontal,
  onChange,
  gap,
}: {
  model: RadioFieldModel<T>;
} & Partial<RadioButtonGroupProps<T>>) {
  const { t } = useTranslation();
  return (
    <>
      <Box vertical={!horizontal} gap={horizontal ? (gap ?? "md") : (gap ?? "sm")}>
        {(options ?? model.options).map((option) => (
          <RadioButton
            key={(idFn ?? model.getId)(option)}
            name={name}
            disabled={disabled || disabledFn?.(option) || model.isDisabled(option)}
            label={labelFn?.(option) ?? model.getLabel(option)}
            description={descriptionFn ? descriptionFn(option) : model.getDescription(option)}
            onChange={(event) => {
              if (event.target.checked) {
                if (onChange) {
                  onChange(event, option);
                } else {
                  model.setValue(option);
                }
              }
            }}
            checked={model.getId(model.value) === model.getId(option)}
          />
        ))}
      </Box>
      {model.error !== undefined && <Alert type={"error"} message={<>{t(model.error)}</>} />}
    </>
  );
});
